import { create } from 'zustand'
import { TrialInvitation } from '../types'
import { apiClient } from '../utils/api'

interface TrialStore {
  invitations: TrialInvitation[]
  loading: boolean
  error: string | null
  fetchInvitations: (playerId: string) => Promise<void>
  sendInvitation: (invitation: {
    player_id: string
    club_id: string
    club_name: string
    venue: string
    trial_date: string
    duration?: string
    contact_info?: string
    notes?: string
  }) => Promise<void>
  respondToInvitation: (id: string, status: 'accepted' | 'declined') => Promise<void>
}

export const useTrialStore = create<TrialStore>()(
  (set) => ({
    invitations: [],
    loading: false,
    error: null,

    fetchInvitations: async (playerId: string) => {
      set({ loading: true, error: null })
      try {
        const invitations = await apiClient.getTrialInvitations(playerId)
        set({ invitations, loading: false })
      } catch (error) {
        set({ error: 'Failed to load trial invitations', loading: false })
        console.error('Failed to fetch trial invitations:', error)
      }
    },

    respondToInvitation: async (id: string, status: 'accepted' | 'declined') => {
      set({ error: null })
      try {
        await apiClient.respondToTrialInvitation(id, status)
        set((state) => ({
          invitations: state.invitations.map((inv) =>
            inv.id === id ? { ...inv, status } : inv
          ),
        }))
      } catch (error) {
        set({ error: `Failed to ${status} invitation` })
        console.error(`Failed to ${status} invitation:`, error)
        throw error
      }
    },

    sendInvitation: async (invitation) => {
      set({ error: null })
      try {
        const newInvitation = await apiClient.createTrialInvitation(invitation)
        set((state) => ({
          invitations: [newInvitation, ...state.invitations],
        }))
      } catch (error) {
        set({ error: 'Failed to send invitation' })
        console.error('Failed to send invitation:', error)
        throw error
      }
    },
  })
)
